import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { CareersService } from '../../../services/careers.service';
import { Job } from '../../../models/job.model';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-career-details',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './career-details.html',
  styleUrl: './career-details.scss'
})
export class CareerDetails implements OnInit {
  job: Job | null = null;
  loading = false;
  error: string | null = null;
  submitting = false;

  // Reactive Form
  applicationForm: FormGroup;

  constructor(
    private route: ActivatedRoute, 
    private careersService: CareersService,
    private fb: FormBuilder,
    private toastr: ToastrService
  ) {
    this.applicationForm = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      phone: ['', [Validators.required, Validators.minLength(10)]],
      resume: [null, [Validators.required]]
    });
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    const id = idParam ? Number(idParam) : null;
    if (!id) {
      this.error = 'Invalid job id';
      return;
    }

    // Fast-path: if navigation included the job in history.state, use it immediately.
    const navState = window.history.state as { job?: Job } | undefined;
    if (navState && navState.job && navState.job.id === id) {
      this.job = navState.job;
      return; // done — no network call
    }

    // Otherwise fetch from service
    this.loading = true;
    this.careersService.getJobById(id).subscribe({
      next: job => {
        this.job = job;
        this.loading = false;
      },
      error: err => {
        this.error = 'Failed to load job details';
        this.loading = false;
      }
    });
  }

  onResumeSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      const allowedExtensions = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      
      const isValidType = allowedTypes.includes(file.type);
      const isValidExtension = allowedExtensions.includes(fileExtension);
      
      if (!isValidType && !isValidExtension) {
        this.toastr.error('Please upload a PDF, DOC, or DOCX file.');
        event.target.value = '';
        return;
      }
      
      // Validate file size (5MB limit)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        this.toastr.error('File size must be less than 5MB.');
        event.target.value = '';
        return;
      }
      
      this.applicationForm.patchValue({ resume: file });
      this.resume?.markAsTouched();
    }
  }

  submitApplication() {
    if (this.applicationForm.invalid || !this.job) {
      this.toastr.error('Please fill in all required fields correctly.');
      return;
    }

    this.submitting = true;
    const formValue = this.applicationForm.value;

    // Prepare application data
    const applicationData = {
      firstname: formValue.firstName.trim(),
      lastname: formValue.lastName.trim(),
      email: formValue.email.trim(),
      mobile: formValue.phone.trim().replace(/\D/g, ''),
      resume: formValue.resume,
      job_code: this.job.id,
      company: this.job.company_name || environment.companyName
    };

    this.careersService.submitJobApplication(applicationData).subscribe({
      next: (response) => {
        this.submitting = false;
        
        if (response?.status === true) {
          const msg = response.message || 'Application submitted successfully!';
          this.toastr.success(msg);
          
          // Reset form
          this.applicationForm.reset();
          
          // Clear file input
          const fileInput = document.getElementById('resumeFile') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = '';
          }
        } else {
          const errorMsg = response?.message || 'Failed to submit application.';
          this.toastr.error(errorMsg);
        }
      },
      error: (error) => {
        this.submitting = false;
        console.error('Submission error:', error);
        
        let errorMsg = 'Failed to submit application. Please try again.';
        if (error.status === 500) {
          errorMsg = 'Server error. Please try again later.';
        } else if (error.status === 413) {
          errorMsg = 'File too large. Please upload a smaller file.';
        } else if (error.status === 400) {
          errorMsg = 'Invalid data. Please check your information and try again.';
        } else if (error.status === 422) {
          errorMsg = 'Validation error. Please check your information and try again.';
        }
        
        this.toastr.error(errorMsg);
      }
    });
  }

  // Helper methods for template
  get firstName() { return this.applicationForm.get('firstName'); }
  get lastName() { return this.applicationForm.get('lastName'); }
  get email() { return this.applicationForm.get('email'); }
  get phone() { return this.applicationForm.get('phone'); }
  get resume() { return this.applicationForm.get('resume'); }

  getResumeFileName(): string {
    const resumeValue = this.resume?.value;
    return resumeValue && resumeValue.name ? resumeValue.name : '';
  }
}
